/**
 * Cart JS - Complete Cart Operations
 * Handles: Remove items, Update quantities, Cart notes
 */

// Helper function to update all cart sections
async function updateCartSections(cart) {
  try {
    // Always fetch drawer and icon sections
    const sectionIds = ['cart-drawer', 'cart-icon-bubble'];

    // If on the cart page, also refresh the cart table (but NOT footer — see below)
    const cartPageItems = document.getElementById('main-cart-items');
    const cartPageFooter = document.getElementById('main-cart-footer');
    if (cartPageItems) sectionIds.push('main-cart-items');
    // Don't fetch main-cart-footer — Section Rendering API can't resolve
    // its block config (subtotal, buttons) from cart.json, which destroys
    // the checkout button. We update the subtotal from cart JSON instead.

    // Use current page URL so Shopify renders sections with correct cart context
    const currentPath = window.location.pathname;
    const response = await fetch(`${currentPath}?sections=${sectionIds.join(',')}`);
    const sections = await response.json();
    const parser = new DOMParser();
    
    // Update cart drawer content
    if (sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const newDrawer = doc.querySelector('#CartDrawer');
      const currentDrawer = document.querySelector('#CartDrawer');
      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      }
    }
    
    // Update cart icon bubble
    if (sections['cart-icon-bubble']) {
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newIcon = doc.querySelector('#cart-icon-bubble');
      const currentIcon = document.querySelector('#cart-icon-bubble');
      if (newIcon && currentIcon) {
        currentIcon.innerHTML = newIcon.innerHTML;
      }
    }

    // Update cart page items table
    if (sections['main-cart-items'] && cartPageItems) {
      const doc = parser.parseFromString(sections['main-cart-items'], 'text/html');
      const newItems = doc.getElementById('main-cart-items');
      if (newItems) {
        cartPageItems.innerHTML = newItems.innerHTML;
      }
    }

    // Update subtotal in footer from cart JSON (preserves checkout button + blocks)
    if (cartPageFooter && cart && cart.total_price !== undefined) {
      const subtotalEl = cartPageFooter.querySelector('.totals__subtotal-value');
      if (subtotalEl) {
        // Format price: cart.total_price is in cents
        const cents = cart.total_price;
        const formatted = (cents / 100).toFixed(2);
        // Preserve currency prefix from existing text
        const ref = subtotalEl.textContent.trim();
        const prefix = ref.match(/^[^\d]*/) ? ref.match(/^[^\d]*/)[0] : '';
        subtotalEl.textContent = prefix + formatted;
      }
    }
    
    // Update empty state on drawer
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (cart && cart.item_count === 0) {
        drawer.classList.add('is-empty');
      } else {
        drawer.classList.remove('is-empty');
      }
    }

    // Update empty state on cart page
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) {
      if (cart && cart.item_count === 0) {
        cartItemsEl.classList.add('is-empty');
      } else {
        cartItemsEl.classList.remove('is-empty');
      }
    }
  } catch (error) {
    console.error('Error updating cart sections:', error);
  }
}

// ============ CART REMOVE BUTTON ============
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.boundClickHandler = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.boundClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClickHandler);
  }

  onButtonClick(event) {
    // Check if click is on the button or inside it
    if (event.target.closest('button')) {
      event.preventDefault();
      this.removeItem();
    }
  }

  async removeItem() {
    const index = this.dataset.index || this.querySelector('button')?.dataset?.index;
    if (!index) {
      console.warn('CartRemoveButton: No data-index attribute found');
      return;
    }
    await removeCartLineItem(index, this.querySelector('button'));
  }
}

// Shared remove helper for delegated clicks
async function removeCartLineItem(index, button) {
  const line = parseInt(index, 10);
  if (Number.isNaN(line)) return;

  if (button) {
    button.disabled = true;
    button.classList.add('loading');
  }

  try {
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line,
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error('Failed to remove item');
    }

    const cart = await response.json();
    await updateCartSections(cart);
  } catch (error) {
    console.error('Error removing item:', error);
    if (button) {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }
}

// Delegated handler to ensure remove works even before custom elements upgrade
if (!window.__cartRemoveHandlerBound) {
  window.__cartRemoveHandlerBound = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-remove]');
    if (!button) return;
    event.preventDefault();
    const index = button.dataset.index;
    if (!index) return;
    removeCartLineItem(index, button);
  });
}

// ============ CART ITEMS (for quantity changes) ============
// Shared quantity update function
async function updateCartQuantity(line, quantity, container) {
  if (container) container.classList.add('cart__items--disabled');

  try {
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      throw new Error('Failed to update quantity');
    }

    const cart = await response.json();
    await updateCartSections(cart);

  } catch (error) {
    console.error('Error updating quantity:', error);
  } finally {
    if (container) container.classList.remove('cart__items--disabled');
  }
}

class CartItems extends HTMLElement {
  // Quantity changes are handled by theme.liquid's delegated handler
  // (optimistic UI + debounced server sync). No duplicate listeners here.
  connectedCallback() {}
}

// Quantity +/- clicks and change events are handled by theme.liquid's
// delegated handlers (optimistic UI + single debounced server sync).
// Removed duplicate delegated handlers that were causing 3-4 concurrent
// updateCartQuantity() calls per single click.

// ============ CART NOTE ============
class CartNote extends HTMLElement {
  connectedCallback() {
    const textarea = this.querySelector('textarea');
    if (textarea) {
      textarea.addEventListener('change', (e) => {
        this.saveNote(e.target.value);
      });
    }
  }

  async saveNote(note) {
    try {
      await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note })
      });
    } catch (error) {
      console.error('Error saving note:', error);
    }
  }
}

// ============ REGISTER CUSTOM ELEMENTS ============
if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', class extends CartItems {});
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
